import { useEffect, useState, useCallback } from "react";
import { api, formatApiError, buildInviteUrl, API_BASE } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Copy, Trash2, UserPlus, Plus, Shield, Crown, ClipboardList, Sliders, CalendarPlus, Download, Share2 } from "lucide-react";
import AddMemberModal from "../components/AddMemberModal";
import CreateGroupModal from "../components/CreateGroupModal";
import LoadRaceCardModal from "../components/LoadRaceCardModal";
import CompetitionSettingsModal from "../components/CompetitionSettingsModal";
import RaceManagementList from "../components/RaceManagementList";
import ShareInvitesModal from "../components/ShareInvitesModal";
import PicksWatchdog from "../components/PicksWatchdog";
import { colourHex } from "../lib/constants";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../components/ui/alert-dialog";

function ApproveToggle({ approved, onToggle, disabled, testId }) {
  return (
    <button
      onClick={onToggle}
      disabled={disabled}
      data-testid={testId}
      aria-pressed={approved}
      className={`px-3 py-1.5 rounded-full text-xs font-bold border transition-colors active:scale-95 ${
        approved
          ? "bg-[#2f9e44]/10 text-[#2f9e44] border-[#2f9e44]/25"
          : "bg-[#f8f9fa] text-[#6c757d] border-[#e9ecef]"
      } disabled:opacity-60`}
    >
      {approved ? "✓ Approved" : "Pending"}
    </button>
  );
}

export default function AdminTab({ group, onGroupCreated, onGroupUpdated, isOwner }) {
  const { user } = useAuth();
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [loadCardOpen, setLoadCardOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [pendingRemove, setPendingRemove] = useState(null);
  const [raceRefresh, setRaceRefresh] = useState(0);
  const [shareOpen, setShareOpen] = useState(false);

  const load = useCallback(async () => {
    if (!group) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/groups/${group.id}/members`);
      setMembers(data.members);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail, "Failed to load members"));
    } finally { setLoading(false); }
  }, [group]);

  useEffect(() => { load(); }, [load]);

  const toggleApprove = async (m) => {
    try {
      const { data } = await api.patch(`/groups/${group.id}/members/${m.user_id}`, {
        approved: !m.approved,
      });
      setMembers((prev) => prev.map((x) => (x.user_id === m.user_id ? data.member : x)));
      toast.success(`${data.member.display_name} ${data.member.approved ? "approved" : "unapproved"}`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail, "Failed to update"));
    }
  };

  const copyInvite = async (m) => {
    if (!m.invite_link_path) {
      toast.error("This member logs in with email/password — no invite link.");
      return;
    }
    const url = buildInviteUrl(m.invite_link_path);
    try {
      await navigator.clipboard.writeText(url);
      toast.success(`Copied ${m.display_name}'s invite link`);
    } catch {
      toast.error("Could not copy — long-press the link to copy manually");
    }
  };

  const confirmRemove = async () => {
    const m = pendingRemove;
    setPendingRemove(null);
    if (!m) return;
    try {
      await api.delete(`/groups/${group.id}/members/${m.user_id}`);
      setMembers((prev) => prev.filter((x) => x.user_id !== m.user_id));
      toast.success(`${m.display_name} removed`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail, "Failed to remove"));
    }
  };

  const canManage = isOwner || (group && members?.some?.((m) => m.user_id === user.id && m.is_admin));

  if (!group) {
    return (
      <div className="px-4 pt-4" data-testid="admin-no-group">
        <div className="bg-white border border-[#e9ecef] rounded-2xl p-6 text-center">
          <div className="text-4xl mb-2">🐎</div>
          <div className="font-display font-extrabold text-lg">No group yet</div>
          <p className="text-sm text-[#6c757d] mt-1">Create one to get started.</p>
          <button
            onClick={() => setCreateOpen(true)}
            className="mt-4 bg-[#1971c2] hover:bg-[#1864ab] text-white font-bold rounded-xl px-5 py-2.5 active:scale-95 transition-transform"
            data-testid="create-first-group-btn"
          >
            Create group
          </button>
        </div>
        <CreateGroupModal open={createOpen} onClose={() => setCreateOpen(false)} onCreated={onGroupCreated} />
      </div>
    );
  }

  return (
    <div className="px-4 pt-4 space-y-4 pb-24" data-testid="admin-tab-content">
      {/* Group summary card */}
      <div className="bg-white border border-[#e9ecef] rounded-2xl p-4 shadow-[0_4px_20px_-4px_rgba(0,0,0,0.05)]">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <span className="w-3 h-3 rounded-full" style={{ backgroundColor: colourHex(group.colour) }} />
              <div className="font-display font-extrabold text-xl truncate">{group.name}</div>
            </div>
            <div className="flex items-center gap-2 mt-1.5 flex-wrap">
              <span className="text-[10px] font-bold uppercase tracking-wider bg-[#f8f9fa] border border-[#e9ecef] rounded-full px-2 py-0.5 text-[#6c757d]">
                Code {group.invite_code}
              </span>
              <span className="text-[10px] font-bold uppercase tracking-wider bg-[#f8f9fa] border border-[#e9ecef] rounded-full px-2 py-0.5 text-[#6c757d]">
                Wildcards {group.wildcards_per_participant}
              </span>
              <span className="text-[10px] font-bold uppercase tracking-wider bg-[#f8f9fa] border border-[#e9ecef] rounded-full px-2 py-0.5 text-[#6c757d]">
                {group.season_weeks}wk season
              </span>
              {(group.prize_1st || group.prize_2nd || group.prize_3rd) && (
                <span className="text-[10px] font-bold uppercase tracking-wider bg-[#c07800]/10 border border-[#c07800]/25 rounded-full px-2 py-0.5 text-[#c07800]">
                  💰 Prizes
                </span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Actions */}
      {canManage && (
        <div className="grid grid-cols-2 gap-3">
          <button
            onClick={() => setLoadCardOpen(true)}
            className="bg-[#1971c2] hover:bg-[#1864ab] text-white font-display font-bold rounded-2xl py-3.5 px-3 flex items-center justify-center gap-2 shadow-md active:scale-95 transition-transform"
            data-testid="load-race-card-button"
          >
            <CalendarPlus className="w-5 h-5" strokeWidth={2.5} />
            Load racing card
          </button>
          <button
            onClick={() => setSettingsOpen(true)}
            className="bg-white text-[#212529] border-2 border-[#e9ecef] hover:bg-[#f8f9fa] font-display font-bold rounded-2xl py-3.5 px-3 flex items-center justify-center gap-2 active:scale-95 transition-transform"
            data-testid="competition-settings-button"
          >
            <Sliders className="w-5 h-5" strokeWidth={2.5} />
            Competition
          </button>
          <button
            onClick={() => setAddOpen(true)}
            className="bg-white text-[#212529] border-2 border-[#e9ecef] hover:bg-[#f8f9fa] font-display font-bold rounded-2xl py-3.5 px-3 flex items-center justify-center gap-2 active:scale-95 transition-transform"
            data-testid="add-member-button"
          >
            <UserPlus className="w-5 h-5" strokeWidth={2.5} />
            Add member
          </button>
          <button
            onClick={() => setCreateOpen(true)}
            className="bg-white text-[#212529] border-2 border-[#e9ecef] hover:bg-[#f8f9fa] font-display font-bold rounded-2xl py-3.5 px-3 flex items-center justify-center gap-2 active:scale-95 transition-transform"
            data-testid="create-group-button"
          >
            <Plus className="w-5 h-5" strokeWidth={2.5} />
            New group
          </button>
        </div>
      )}

      {/* Mock data notice */}
      <div className="rounded-2xl bg-[#fff4e6] border border-[#c07800]/25 p-3 text-[11px] font-semibold text-[#c07800]" data-testid="mock-data-notice">
        Race fields shown here are mock Australian data. Once the Betfair API key is provided, live fields will load automatically. Dividends will still be entered manually from Sportsbet / Neds / Ladbrokes / TAB after each race.
      </div>

      {/* Picks watchdog */}
      {canManage && <PicksWatchdog group={group} />}

      {canManage && (
        <a
          href={`${API_BASE}/groups/${group.id}/export.csv`}
          data-testid="csv-export-link"
          className="block w-full bg-white text-[#212529] border-2 border-[#e9ecef] hover:bg-[#f8f9fa] font-display font-bold rounded-2xl py-3 px-3 flex items-center justify-center gap-2 active:scale-95 transition-transform"
        >
          <Download className="w-5 h-5" strokeWidth={2.5} />
          Export CSV
        </a>
      )}

      {/* Race Management */}
      <div>
        <div className="flex items-center justify-between px-1 mb-2">
          <h3 className="font-display font-extrabold text-lg flex items-center gap-1.5">
            <ClipboardList className="w-5 h-5" strokeWidth={2.5} /> Race management
          </h3>
        </div>
        <RaceManagementList group={group} refreshTick={raceRefresh} />
      </div>

      {/* Members list */}
      <div>
        <div className="flex items-center justify-between px-1 mb-2">
          <h3 className="font-display font-extrabold text-lg">Members</h3>
          <div className="flex items-center gap-2">
            {canManage && members.some((m) => m.invite_link_path) && (
              <button
                onClick={() => setShareOpen(true)}
                data-testid="share-invites-button"
                className="bg-white border border-[#e9ecef] hover:bg-[#f8f9fa] text-[#1971c2] font-bold text-xs rounded-full px-3 py-1.5 flex items-center gap-1 active:scale-95 transition-transform min-h-[36px]"
              >
                <Share2 className="w-3.5 h-3.5" strokeWidth={2.5} /> Share invites
              </button>
            )}
            <span className="text-xs font-bold text-[#6c757d]">{members.length}</span>
          </div>
        </div>

        {loading ? (
          <div className="bg-white border border-[#e9ecef] rounded-2xl p-6 text-center text-sm text-[#6c757d]">
            Loading members…
          </div>
        ) : (
          <div className="space-y-3" data-testid="members-list">
            {members.map((m) => (
              <div
                key={m.user_id}
                className="bg-white border border-[#e9ecef] rounded-2xl p-4 shadow-[0_4px_20px_-4px_rgba(0,0,0,0.04)]"
                data-testid={`member-card-${m.user_id}`}
              >
                <div className="flex items-center gap-3">
                  <div className="w-14 h-14 rounded-full bg-[#f8f9fa] border border-[#e9ecef] flex items-center justify-center text-3xl shrink-0">
                    {m.avatar_emoji || "🐎"}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <div className="font-display font-extrabold text-base truncate">{m.display_name}</div>
                      {m.is_owner && (
                        <span className="inline-flex items-center gap-0.5 text-[9px] font-black uppercase tracking-wider bg-[#c07800]/10 text-[#c07800] border border-[#c07800]/25 rounded-full px-1.5 py-0.5">
                          <Crown className="w-3 h-3" strokeWidth={2.5} /> Owner
                        </span>
                      )}
                      {m.is_admin && !m.is_owner && (
                        <span className="inline-flex items-center gap-0.5 text-[9px] font-black uppercase tracking-wider bg-[#1971c2]/10 text-[#1971c2] border border-[#1971c2]/25 rounded-full px-1.5 py-0.5">
                          <Shield className="w-3 h-3" strokeWidth={2.5} /> Admin
                        </span>
                      )}
                    </div>
                    <div className="text-[11px] text-[#6c757d] mt-0.5 truncate">
                      {m.email ? m.email : "Magic-link login"}
                    </div>
                  </div>
                  <ApproveToggle
                    approved={m.approved}
                    disabled={!canManage || m.is_owner}
                    onToggle={() => toggleApprove(m)}
                    testId={`approve-toggle-${m.user_id}`}
                  />
                </div>

                {canManage && (
                  <div className="mt-3 pt-3 border-t border-[#e9ecef] flex items-center gap-2">
                    {m.invite_link_path && (
                      <button
                        onClick={() => copyInvite(m)}
                        className="flex-1 bg-[#f8f9fa] hover:bg-[#e9ecef] text-[#212529] font-bold text-xs rounded-lg py-2 px-3 flex items-center justify-center gap-1.5 active:scale-95 transition-transform"
                        data-testid={`copy-invite-${m.user_id}`}
                      >
                        <Copy className="w-3.5 h-3.5" strokeWidth={2.5} />
                        Copy invite link
                      </button>
                    )}
                    {!m.is_owner && (
                      <button
                        onClick={() => setPendingRemove(m)}
                        className="bg-[#fdecec] hover:bg-[#fbdada] text-[#c92a2a] font-bold text-xs rounded-lg py-2 px-3 flex items-center justify-center gap-1.5 active:scale-95 transition-transform"
                        data-testid={`remove-member-${m.user_id}`}
                        aria-label={`Remove ${m.display_name}`}
                      >
                        <Trash2 className="w-3.5 h-3.5" strokeWidth={2.5} />
                        Remove
                      </button>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      <AddMemberModal
        open={addOpen}
        onClose={() => setAddOpen(false)}
        groupId={group.id}
        onAdded={() => load()}
      />
      <CreateGroupModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={onGroupCreated}
      />
      <LoadRaceCardModal
        open={loadCardOpen}
        onClose={() => setLoadCardOpen(false)}
        group={group}
        onLoaded={() => setRaceRefresh((n) => n + 1)}
      />
      <CompetitionSettingsModal
        open={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        group={group}
        onSaved={(g) => onGroupUpdated && onGroupUpdated(g)}
      />
      <ShareInvitesModal
        open={shareOpen}
        onClose={() => setShareOpen(false)}
        members={members}
      />

      <AlertDialog open={!!pendingRemove} onOpenChange={(o) => !o && setPendingRemove(null)}>
        <AlertDialogContent data-testid="remove-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display">Remove {pendingRemove?.display_name}?</AlertDialogTitle>
            <AlertDialogDescription>
              They'll lose access to this group. Their picks history stays in the record.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="remove-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-[#c92a2a] hover:bg-[#a72020]"
              onClick={confirmRemove}
              data-testid="remove-confirm"
            >
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
